import { LOGIN_PAGE_URL } from 'constants/utils';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import uuid from 'react-uuid';

import {
  deleteLsBothTokens,
  deleteLsFeaturesEnrollmentPlanner,
  deleteLsUserRoleObject,
  deleteReportingFilters,
  getLsAccessToken,
  resetClientListPagination
} from './localStorageTokens';
import { apiRefreshToken } from './useRefreshToken';

const userAccessTokenInitial = getLsAccessToken();
const timezoneOffset = Math.floor(Number(new Date().getTimezoneOffset()) / 60);
const timezoneName = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const BACKEND_API_EXTENSION = 'core/';

//PUBLIC ENDPOINTS
export const axiosPublic = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + BACKEND_API_EXTENSION,
  headers: {
    'x-time-zone-offset': timezoneOffset,
    'x-time-zone-name': timezoneName,
    'impact-request-source': window.location.pathname
  }
});

axiosPublic.interceptors.request.use(
  (config: any) => {
    config.headers = {
      ...config.headers,
      'impact-request-source': window.location.pathname,
      'x-impact-correlation-id': uuid(),
      'x-time-zone-name': timezoneName
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

//PRIVATE ENDPOINTS
export const axiosPrivate = axios.create({
  baseURL: process.env.REACT_APP_BACKEND_URL + BACKEND_API_EXTENSION,
  headers: {
    Authorization: `Bearer ${userAccessTokenInitial}`,
    'x-time-zone-offset': timezoneOffset,
    'impact-request-source': window.location.pathname,
    'x-time-zone-name': timezoneName
  }
});

axiosRetry(axiosPrivate, {
  retries: 0,
  shouldResetTimeout: true,
  retryDelay: (retryCount, error) =>
    axiosRetry.exponentialDelay(retryCount, error, 1000) + Math.floor(Math.random() * 250),
  retryCondition: (error) => {
    const status = error.response?.status;

    return (
      axiosRetry.isNetworkError(error) ||
      status === 408 ||
      status === 429 ||
      (status != null && status >= 500)
    );
  }
});
//initial req, when authorization header is not set
axiosPrivate.interceptors.request.use(
  (config: any) => {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${getLsAccessToken()}`,
      'impact-request-source': window.location.pathname,
      'x-impact-correlation-id': uuid(),
      'x-time-zone-name': timezoneName
    };

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const logout = () => {
  deleteLsBothTokens();
  deleteLsUserRoleObject();
  deleteReportingFilters();
  deleteLsFeaturesEnrollmentPlanner();
  resetClientListPagination();
  window.location.href = `${window.location.origin + LOGIN_PAGE_URL}`;
};

axiosPrivate.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error?.config;

    //is 401 and  prev request was not sent to prevent loop - retry only once
    // if (false && !originalRequest?.sent) {
    if (error?.response?.status === 401 && !originalRequest?.sent) {
      originalRequest.sent = true;

      const userAccessToken = getLsAccessToken();
      const newAccessToken = await apiRefreshToken(userAccessToken);

      if (!newAccessToken) {
        // logout();

        return Promise.reject(error);
      }

      originalRequest.headers = {
        ...originalRequest.headers,
        'Access-Control-Allow-Origin': '*',
        Authorization: `Bearer ${newAccessToken}`
      };
      return axiosPrivate(originalRequest);
    }

    // if after the token refresh, the api still returns 401,
    // we want to logout the user
    if (error?.response?.status === 401 && originalRequest?.sent) {
      logout();
    }

    return Promise.reject(error);
  }
);
